import { motion } from 'framer-motion';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { useNavigate } from 'react-router-dom';

// Padrão visual compartilhado com as seções da home
const eyebrowSx = (dark) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 1.75,
    py: 0.75,
    borderRadius: "999px",
    backgroundColor: dark ? "rgba(255, 200, 69, 0.12)" : "rgba(255, 200, 69, 0.25)",
    border: dark ? "1px solid rgba(255, 200, 69, 0.4)" : "none",
    color: dark ? "#ffc845" : "#0b243d",
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
});

const primaryButtonSx = {
    px: 4,
    py: 1.75,
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    backgroundColor: "#ffc845",
    color: "#0b243d",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(255, 200, 69, 0.35)",
    transition: "all 0.2s ease",
    '&:hover': {
        backgroundColor: "#e9b53d",
        transform: "translateY(-2px)",
        boxShadow: "0 10px 28px rgba(255, 200, 69, 0.45)",
    },
};

const WhatWeDo = (props) => {
    const navigate = useNavigate();
    const listAccordions = [
        {
            title: "Entrega de obrigações fiscais", icon: FactCheckOutlinedIcon, desc: (
                <>
                    Cumprir suas obrigações fiscais de forma <b>precisa e pontual</b> é essencial para <b>proteger seu patrimônio</b> e manter sua clínica em <b>conformidade</b>. Nós cuidamos disso por você, com <b>rigor técnico e tranquilidade</b>.
                </>
            )
        },
        {
            title: "Folha de pagamento e pró-labore", icon: PaymentsOutlinedIcon, desc: (
                <>
                    Gestão completa da folha de pagamento e do pró-labore, <b>garantindo cálculos corretos, segurança jurídica e a tranquilidade</b> de que sua equipe está sempre valorizada.
                </>
            )
        },
        {
            title: "Consultoria e emissão de parecer contábil", icon: InsightsOutlinedIcon, desc: (
                <>
                    Mais do que números, oferecemos <b>clareza estratégica</b>. Nossos pareceres contábeis são elaborados com <b>profundidade técnica</b> e traduzidos em linguagem simples, para que você <b>tenha decisões seguras em mãos</b>.
                </>
            )
        },
        {
            title: "Abertura e regularização de clínicas e consultórios", icon: StorefrontOutlinedIcon, desc: (
                <>
                    Transformamos a complexidade burocrática em um processo fluido. Da abertura ao pleno funcionamento, sua clínica ou consultório nasce <b>regularizado, sólido e pronto para crescer</b>.
                </>
            )
        },
        {
            title: "Emissão de Notas Fiscais de serviços de saúde", icon: ReceiptLongOutlinedIcon, desc: (
                <>
                    <b>Agilidade e precisão na emissão das suas notas fiscais</b>. Tudo de acordo com a legislação, sem fricções, para que você mantenha sua prática profissional organizada e confiável.
                </>
            )
        },
        { title: "Atendimento especializado e humanizado", icon: SupportAgentOutlinedIcon, desc: "Cada profissional de saúde tem uma história única. Nosso atendimento respeita essa singularidade, unindo escuta ativa, proximidade e soluções contábeis feitas sob medida." },
    ];

    return (
        <>
            {/* SECTION 1 */}
            <Box
                component="section"
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: "#f9f2e4",
                    py: { xs: 8, md: 12 },
                }}
            >
                <Box
                    sx={{
                        width: "90%",
                        maxWidth: "1280px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
                        alignItems: "start",
                        gap: { xs: 5, md: 8 },
                    }}
                >
                    {/* Lado Esquerdo (fica fixo ao rolar no desktop) */}
                    <Box sx={{ position: { md: "sticky" }, top: { md: "120px" } }}>
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            <Box sx={{ ...eyebrowSx(false), mb: 2.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
                                O que fazemos
                            </Box>

                            <h2 style={{ margin: "0 0 20px", fontSize: "clamp(1.8rem, 3.2vw, 2.5rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", color: "#0b243d" }}>
                                Cuidamos da parte fiscal para que você foque no{" "}
                                <span style={{ color: "#c8961e" }}>cuidado das pessoas.</span>
                            </h2>

                            <Box component="p" sx={{ m: 0, fontSize: { xs: "1rem", md: "1.08rem" }, lineHeight: 1.7, color: "#4b5563" }}>
                                Contabilidade simples, experiente e personalizada: <b style={{ color: "#0b243d" }}>profissionalizamos</b> sua atividade, consultório ou clínica de forma <b style={{ color: "#0b243d" }}>100% online e sem burocracias.</b>
                            </Box>

                            <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {["100% online", "Sem burocracia", "Feito para a saúde"].map((tag) => (
                                    <Box
                                        key={tag}
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: "999px",
                                            backgroundColor: "#fff",
                                            border: "1px solid #ece4d3",
                                            fontSize: "0.85rem",
                                            fontWeight: 600,
                                            color: "#0b243d",
                                        }}
                                    >
                                        <CheckIcon sx={{ fontSize: 16, color: "#c8961e" }} />
                                        {tag}
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                                <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("../Planos")}>
                                    Contrate agora
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate("../Solucoes")}
                                    sx={{
                                        px: 4,
                                        py: 1.75,
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        color: "#0b243d",
                                        borderColor: "rgba(11, 36, 61, 0.3)",
                                        borderRadius: "10px",
                                        transition: "all 0.2s ease",
                                        '&:hover': {
                                            borderColor: "#0b243d",
                                            backgroundColor: "rgba(11, 36, 61, 0.04)",
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    Ver soluções
                                </Button>
                            </Box>
                        </motion.div>
                    </Box>

                    {/* Lado Direito (Accordions) */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {listAccordions.map((obj, i) => {
                            const Icon = obj.icon;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                >
                                    <Accordion
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            backgroundColor: "#fff",
                                            border: "1px solid #ece4d3",
                                            borderRadius: "14px !important",
                                            overflow: "hidden",
                                            transition: "all 0.2s ease",
                                            "&:before": { display: "none" },
                                            "&:hover": { borderColor: "#ffc845" },
                                            "&.Mui-expanded": {
                                                borderColor: "#ffc845",
                                                boxShadow: "0 12px 30px rgba(11, 36, 61, 0.08)",
                                            },
                                            "&.Mui-expanded .wwd-icon": {
                                                backgroundColor: "#0b243d",
                                                color: "#ffc845",
                                            },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: "#0b243d" }} />}
                                            aria-controls={`panel${i}-content`}
                                            id={`panel${i}-header`}
                                            sx={{ px: { xs: 2, md: 2.5 }, py: 1, "& .MuiAccordionSummary-content": { my: 1 } }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Box
                                                    className="wwd-icon"
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: "12px",
                                                        backgroundColor: "rgba(255, 200, 69, 0.2)",
                                                        color: "#0b243d",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                        transition: "all 0.2s ease",
                                                    }}
                                                >
                                                    <Icon sx={{ fontSize: 22 }} />
                                                </Box>
                                                <Typography component="span" sx={{ color: "#0b243d", fontWeight: 700, fontSize: { xs: "0.95rem", md: "1.02rem" }, lineHeight: 1.35 }}>
                                                    {obj.title}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                px: { xs: 2, md: 2.5 },
                                                pt: 0,
                                                pb: 2.5,
                                                pl: { xs: 2, md: "88px" },
                                                color: "#4b5563",
                                                fontSize: "0.95rem",
                                                lineHeight: 1.7,
                                                "& b": { color: "#0b243d" },
                                            }}
                                        >
                                            {obj.desc}
                                        </AccordionDetails>
                                    </Accordion>
                                </motion.div>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* SECTION 2 */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                    color: "#fff",
                    overflow: "hidden",
                }}
            >
                {/* Detalhe decorativo */}
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: -90, md: -120 },
                        right: { xs: -90, md: -120 },
                        width: { xs: 220, md: 320 },
                        height: { xs: 220, md: 320 },
                        borderRadius: "50%",
                        border: { xs: "36px solid #ffc845", md: "52px solid #ffc845" },
                        opacity: 0.12,
                        pointerEvents: "none",
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        width: "90%",
                        maxWidth: "1280px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "4fr 8fr" },
                        alignItems: "center",
                        gap: { xs: 0, md: 6 },
                    }}
                >
                    {/* Imagem */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <Box
                            component="img"
                            src="/assets/lora medica.jpeg"
                            alt="Profissional da saúde"
                            sx={{
                                display: { xs: "none", md: "block" },
                                width: "100%",
                                maxWidth: "460px",
                                mt: 4,
                                objectFit: "contain",
                                maskImage: "linear-gradient(to bottom, rgba(0, 0, 0, 1) 70%, rgba(0, 0, 0, 0))",
                                WebkitMaskImage: "linear-gradient(to bottom, rgba(0, 0, 0, 1) 70%, rgba(0, 0, 0, 0))",
                            }}
                        />
                    </motion.div>

                    {/* Texto + botão */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <Box sx={{ py: { xs: 8, md: 6 }, textAlign: { xs: "center", md: "left" } }}>
                            <Box sx={{ ...eyebrowSx(true), mb: 2.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
                                Foque no que importa
                            </Box>

                            <h2 style={{ margin: "0 0 16px", fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 800, lineHeight: 1.25, letterSpacing: "-0.5px", color: "#fff" }}>
                                Cuidamos da sua contabilidade para que você se concentre no{" "}
                                <span style={{ color: "#ffc845" }}>que faz de melhor.</span>
                            </h2>

                            <Box component="p" sx={{ m: { xs: "0 auto", md: 0 }, maxWidth: "560px", fontSize: { xs: "1rem", md: "1.08rem" }, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.8)" }}>
                                Escolha o plano ideal para a sua rotina e deixe impostos, notas e obrigações com quem entende da área da saúde.
                            </Box>

                            <Box
                                sx={{
                                    mt: 4,
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    alignItems: "center",
                                    justifyContent: { xs: "center", md: "flex-start" },
                                    gap: { xs: 1.5, sm: 3 },
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => props.scrollToPlans ? props.scrollToPlans() : navigate("../Planos")}
                                    sx={{ ...primaryButtonSx, width: { xs: "100%", sm: "auto" } }}
                                >
                                    Ver os planos
                                </Button>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgba(255, 255, 255, 0.75)", fontSize: "0.9rem" }}>
                                    <CheckIcon sx={{ fontSize: 18, color: "#ffc845" }} />
                                    Sem multa e sem fidelidade
                                </Box>
                            </Box>
                        </Box>
                    </motion.div>
                </Box>
            </Box>
        </>
    );
};

export default WhatWeDo;
